import { useEffect, useMemo, useRef } from "react";
import useSWRImmutable from "swr/immutable";
import { GraphType, TestGraph } from "../types";
import { calcEditableValuesGraphsDiff, isO2PulseGraph } from "../utils";
import { mutate as globalMutate, SWRConfiguration } from "swr";
import { useSWRAlwaysRevalidate } from "./useSWR_allwaysRevalidate";
import _, { omitBy, isUndefined } from "lodash";
import { GraphsContextType } from "../context/GraphsProvider";

type UnsavedData = Array<{
    graph_id: string;
    changes: Partial<TestGraph> | null;
}>;

export default function useGraphsFactory(
    fetcher: () => Promise<TestGraph[]>,
    swrKey: string | null,
    options?: SWRConfiguration<TestGraph[]> & {
        alwaysRevalidate?: boolean;
        keepUnsavedChanges?: boolean;
    } // Added alwaysRevalidate option
) {
    // map to store the original values of the graphs , by the swrKey.
    const originalValuesRef = useRef<Record<string, TestGraph[] | null>>({});
    const getOriginalValue = () =>
        swrKey ? originalValuesRef.current[swrKey] || null : null;
    const setOriginalValue = (value: TestGraph[] | null) => {
        originalValuesRef.current[swrKey || ""] = value;
    };
    const prevSwrKeyRef = useRef<string | null>(null);
    const globalUnsavedDataRef = useRef<
        Record<
            TestGraph["graph_id"],
            { isO2PulseGraph?: boolean; changes: UnsavedData[0]["changes"] }
        >
    >({});

    const useHook = options?.alwaysRevalidate
        ? useSWRAlwaysRevalidate
        : useSWRImmutable;

    const { data, error, mutate, isLoading } = useHook<TestGraph[]>(
        swrKey,
        fetcher,
        { revalidateOnMount: true, ...options }
    );

    if (!getOriginalValue() && data) {
        setOriginalValue(data);
    }

    const unsavedData: UnsavedData = useMemo(() => {
        const originalValue = getOriginalValue();
        if (!originalValue) {
            return [];
        }
        const changedData = originalValue.map((originalGraph) => {
            const newGraph = data?.find(
                (graph) => graph.graph_id === originalGraph.graph_id
            );
            const unsavedDataEntry =
                globalUnsavedDataRef.current[originalGraph.graph_id];
            if (!newGraph) {
                return {
                    graph_id: originalGraph.graph_id,
                    changes: null,
                };
            }
            const changes = calcEditableValuesGraphsDiff(
                originalGraph,
                newGraph
            );
            if (options?.keepUnsavedChanges) {
                if (swrKey === prevSwrKeyRef.current) {
                    // if peak1 status of o2 pulse graph was changed, apply the change to all other o2 pulse graphs
                    if (
                        unsavedDataEntry?.isO2PulseGraph &&
                        unsavedDataEntry?.changes?.peak1_status !==
                            changes?.peak1_status
                    ) {
                        Object.values(globalUnsavedDataRef.current)
                            .filter((entry) => entry.isO2PulseGraph)
                            .forEach((entry) => {
                                const updatedChanges = omitBy(
                                    {
                                        ...entry.changes,
                                        peak1_status: changes?.peak1_status,
                                    },
                                    isUndefined
                                );
                                entry.changes = _.isEmpty(updatedChanges)
                                    ? null
                                    : (updatedChanges as Partial<TestGraph>);
                            });
                    }
                    const updatedChanges = omitBy(
                        changes,
                        isUndefined
                    ) as typeof changes;
                    globalUnsavedDataRef.current[originalGraph.graph_id] = {
                        isO2PulseGraph:
                            unsavedDataEntry?.isO2PulseGraph ??
                            isO2PulseGraph(originalGraph),
                        changes: _.isEmpty(updatedChanges)
                            ? null
                            : updatedChanges,
                    };
                }
            }
            return {
                graph_id: originalGraph.graph_id,
                changes: changes,
            };
        });
        const filteredChangedData = changedData.filter(
            (item) => item.changes !== null
        );
        return filteredChangedData;
    }, [data, getOriginalValue()]);

    useEffect(() => {
        if (
            !options?.keepUnsavedChanges ||
            swrKey === prevSwrKeyRef.current ||
            !data
        )
            return;

        // Insert the previous unsaved data into the current graphs data
        prevSwrKeyRef.current = swrKey;
        mutate((prev) => {
            if (!prev) return prev;

            const mergedData = prev.map((graph, index) => {
                const prevUnsavedData =
                    globalUnsavedDataRef.current[graph.graph_id]?.changes;

                const originalGraph = getOriginalValue()?.find(
                    (g) => g.graph_id === graph.graph_id
                );
                // if peak1 status of o2 pulse graph was changed, apply the change to all other o2 pulse graphs
                if (isO2PulseGraph(graph) && prevUnsavedData?.peak1_status) {
                    const otherO2PulseGraphs = prev
                        .slice(index)
                        .filter(isO2PulseGraph);
                    otherO2PulseGraphs.forEach((o2PulseGraph) => {
                        const prevUnsavedData_otherGraph =
                            globalUnsavedDataRef.current[o2PulseGraph.graph_id]
                                ?.changes;
                        const changes = {
                            ...prevUnsavedData_otherGraph,
                            peak1_status: prevUnsavedData?.peak1_status,
                        };
                        globalUnsavedDataRef.current[o2PulseGraph.graph_id] = {
                            changes: _.isEmpty(changes) ? null : changes,
                            isO2PulseGraph: true,
                        };
                    });
                }

                if (!prevUnsavedData) {
                    return { ...graph };
                } else {
                    return {
                        ...graph,
                        ...(prevUnsavedData as typeof graph),
                    };
                }
            });
            return mergedData;
        }, false);
    }, [swrKey, data]);

    const processChangesForCancel = (unsavedData: UnsavedData) => {
        const originalValue = getOriginalValue();
        if (!originalValue) return unsavedData;

        const fieldsToBeReverted: (keyof TestGraph)[] = [
            "slope_calculation_begin_value",
            "slope_calculation_end_value",
            "rcp_value",
            "vat_value",
        ];

        return unsavedData.map((item) => {
            const payloadData = { ...item };
            const originalGraph = originalValue.find(
                (graph) => graph.graph_id === payloadData.graph_id
            );

            if (originalGraph && payloadData.changes) {
                // revert payload data to original values, or remove fields that don't need to be reverted
                for (const key in payloadData.changes) {
                    const _key = key as keyof TestGraph;
                    const needToBeReverted = fieldsToBeReverted.includes(_key);
                    if (!needToBeReverted) {
                        delete (payloadData.changes as any)[_key];
                        continue;
                    }
                    (payloadData.changes as any)[_key] = (originalGraph as any)[
                        _key
                    ];
                }
            }
            return payloadData;
        });
    };

    const refreshDataWithUnsavedChanges: GraphsContextType["refreshDataWithUnsavedChanges"] =
        async (additionalData) => {
            const freshData = await fetcher();
            const mergedData = freshData.map((graph) => {
                const unsavedDataEntry =
                    globalUnsavedDataRef.current[graph.graph_id];
                const additionalDataForGraph = additionalData?.[graph.graph_id];
                if (!unsavedDataEntry?.changes) {
                    return {
                        ...graph,
                        ...(additionalDataForGraph as typeof graph),
                    };
                } else {
                    return {
                        ...graph,
                        ...(unsavedDataEntry.changes as typeof graph),
                        ...(additionalDataForGraph as typeof graph),
                    };
                }
            });
            mutate(mergedData, false);
            return mergedData;
        };

    return {
        originalGraphsValue: getOriginalValue(),
        getGlobalUnsavedData: (): UnsavedData =>
            Object.entries(globalUnsavedDataRef.current)
                .filter(([, entry]) => !_.isEmpty(entry?.changes))
                .map(([graph_id, entry]) => ({
                    graph_id,
                    changes: entry.changes,
                })),
        unsavedData,
        setOriginalGraphsValue: setOriginalValue,
        graphs: data || undefined,
        loading: isLoading,
        error,
        mutate,
        processChangesForCancel,
        refreshDataWithUnsavedChanges,
    };
}

export const clearGraphsCache = (graphType?: GraphType) =>
    globalMutate(
        (key) =>
            typeof key === "string" &&
            key.startsWith(`graphs:${graphType || ""}`),
        undefined,
        { revalidate: false }
    );
